import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ACalendar, Input, Label, Select } from 'components/atoms';
import { RadioGroup } from 'components/atoms/RadioGroup/RadioGroup.components';
import CitySelect from 'components/molecules/CitySelect/CitySelect';
import CountrySelect from 'components/molecules/CountrySelect/CountrySelect';
import DepartmentSelect from 'components/molecules/DepartmentSelect/DepartmentSelect';
import { DocumentTypeSelect } from 'components/molecules/DocumentTypeSelect/DocumentTypeSelect';
import { Section } from 'components/molecules/TabPanel/TabPanel';
import useCatalog from 'hooks/useCatalogs/useCatalogs';
import { ReactComponent as WarningIcon } from 'utils/assets/images/warningIcon.svg';
import { catalogoPersonal } from 'utils/helpers/dictionaries';
import { useExternalCodeContext } from 'utils/helpers/ExternalCodesContext';
import { formTypes } from 'utils/interfaces';

import { useProfileFormContext } from '../hooks/useProfileFormContext';

import './PersonalInfo.component.scss';
interface IProps {
  isDisabled: boolean;
  changeData: (value: { name: formTypes; value?: number | string }) => Promise<void>;
  setMarginForCalendar: (state: number) => void;
  isEditing: boolean;
}

export const PersonalInfo = ({
  isDisabled,
  changeData,
  setMarginForCalendar,
  isEditing,
}: IProps) => {
  const { email, profileData } = useProfileFormContext();
  const {
    register,
    getValues,
    control,
    formState: { errors },
    trigger,
    setValue,
  } = useFormContext();
  const { data: bloodGroup } = useCatalog(catalogoPersonal.bloodGroup);
  const formValues = getValues();

  const [documentCountry, setDocumentCountry] = useState<string>('');

  const [isOpenCalendar, setIsOpenCalendar] = useState(false);
  const [showChecks, setShowChecks] = useState<boolean>(false);

  const combineNames = () => {
    const firstNameValue = formValues.firstName || '';
    const lastNameValue = formValues.lastName || '';
    const secondNameValue = formValues.secondName || '';
    const secondLastNameValue = formValues.secondLastName || '';

    const combinedFirstName = secondNameValue
      ? `${firstNameValue} ${secondNameValue}`
      : firstNameValue;
    const combinedLastName = secondLastNameValue
      ? `${lastNameValue} ${secondLastNameValue}`
      : lastNameValue;

    setValue('_combinedFirstName', combinedFirstName, { shouldDirty: false });
    setValue('_combinedLastName', combinedLastName, { shouldDirty: false });

    return {
      firstName: combinedFirstName,
      lastName: combinedLastName,
    };
  };

  const fillDefaultData = () => {
    if (profileData) {
      !formValues.email && changeData({ name: 'email', value: email });

      if (!formValues.firstName && profileData.firstName) {
        const nameParts = profileData.firstName.split(' ');
        const firstName = nameParts[0] || '';
        const secondName = nameParts.slice(1).join(' ') || '';

        changeData({ name: 'firstName', value: firstName });
        changeData({ name: 'secondName', value: secondName });
      }

      setShowChecks(!!formValues.secondName);

      if (!formValues.lastName && profileData.lastName) {
        const lastNameParts = profileData.lastName.split(' ');
        const lastName = lastNameParts[0] || '';
        const secondLastName = lastNameParts.slice(1).join(' ') || '';

        changeData({ name: 'lastName', value: lastName });
        changeData({ name: 'secondLastName', value: secondLastName });
      }

      if (!formValues.preferredName) {
        const preferredName = profileData?.preferredName || formValues.firstName || '';
        changeData({ name: 'preferredName', value: preferredName });
      } else if (
        formValues.preferredName !== formValues.firstName &&
        formValues.preferredName !== formValues.secondName
      ) {
        changeData({ name: 'preferredName', value: formValues.firstName });
      }

      !formValues.bloodType && changeData({ name: 'bloodType', value: profileData.bloodType });

      !formValues.idType && changeData({ name: 'idType', value: profileData.idType });

      !formValues.numberId && changeData({ name: 'numberId', value: profileData.numberId });

      !formValues.bornDate && changeData({ name: 'bornDate', value: profileData.bornDate });

      !formValues.department && changeData({ name: 'state', value: profileData.state });
    }
  };

  useEffect(() => {
    if (profileData && profileData.country) {
      setDocumentCountry(profileData.country || '');
    }
  }, [profileData]);

  useEffect(() => {
    fillDefaultData();
  }, [profileData]);

  useEffect(() => {
    if (!isEditing) {
      combineNames();
    }
  }, [isEditing]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).combineNames = combineNames;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).combineNames;
      }
    };
  }, [formValues.firstName, formValues.lastName, formValues.secondName, formValues.secondLastName]);

  return (
    <Section id={0}>
      <section className='section-form'>
        <aside className='section-form__aside'>
          <div className='section-form__aside--doble-side'>
            <div className='section-form__aside--doble-side-B'>
              <Input
                id='firstName'
                label='Primer nombre'
                placeholder=''
                type='name'
                value={formValues.firstName}
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  changeData({ name: 'firstName', value });
                }}
                onBlur={() => {
                  setShowChecks(formValues.firstName && formValues.secondName);
                  if (!formValues.secondName) {
                    changeData({ name: 'preferredName', value: formValues.firstName || '' });
                  }
                }}
                disabled={isDisabled}
                register={register}
                error={!!errors?.firstName}
              />
            </div>
          </div>
          <div className='section-form__aside--doble-side'>
            <div className='section-form__aside--doble-side-B'>
              <Input
                id='secondName'
                label='Segundo nombre (opcional)'
                placeholder=''
                type='name'
                value={formValues.secondName || ''}
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  changeData({ name: 'secondName', value });
                }}
                onBlur={() => {
                  setShowChecks(formValues.firstName && formValues.secondName);
                  if (!formValues.secondName || !formValues.preferredName) {
                    changeData({ name: 'preferredName', value: formValues.firstName || '' });
                  }
                }}
                disabled={isDisabled}
                register={register}
              />
            </div>
          </div>
        </aside>
        <aside className='section-form__aside'>
          <div className='section-form__aside--doble-side'>
            <div className='section-form__aside--doble-side-B'>
              <Input
                id='lastName'
                label='Primer apellido'
                placeholder=''
                type='name'
                value={formValues.lastName}
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  changeData({ name: 'lastName', value });
                }}
                disabled={isDisabled}
                register={register}
                error={!!errors?.lastName}
              />
            </div>
          </div>
          <div className='section-form__aside--doble-side'>
            <div className='section-form__aside--doble-side-B'>
              <Input
                id='secondLastName'
                label='Segundo apellido (opcional)'
                placeholder=''
                type='name'
                value={formValues.secondLastName || ''}
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  changeData({ name: 'secondLastName', value });
                }}
                disabled={isDisabled}
                register={register}
              />
            </div>
          </div>
        </aside>
        <aside className='section-form__aside'>
          <div className='section-form__aside--doble-side'>
            <div className='section-form__aside--doble-side-B'>
              <Label text={"Nombre preferido"} htmlFor='PreferName' />
              <p className="section-form__text-info">¡Así te llamaremos! Será el nombre que verás en tu correo y en las plataformas de Pragma.</p>
              {formValues.firstName && !formValues.secondName &&
                <div>
                  <Label text={formValues.preferredName} htmlFor='PreferName' className='section-form__one-name' />
                  <p className="section-form__text-info ">(Único nombre registrado)</p>
                </div>
              }
              {showChecks && formValues.firstName && formValues.secondName &&
                <>
                  <div className='section-form__checks'>
                    <RadioGroup
                      id='PreferName'
                      name='PreferName'
                      value={formValues.firstName}
                      checked={formValues.preferredName === formValues.firstName}
                      disabled={isDisabled}
                      onChange={(e) => {
                        const { value } = e.currentTarget;
                        changeData({ name: 'preferredName', value });
                      }}
                    />
                    <Label text={formValues.firstName} htmlFor='PreferName' />
                  </div>
                  <div className='section-form__checks'>
                    <RadioGroup
                      id='PreferName'
                      name='PreferName'
                      value={formValues.secondName}
                      checked={formValues.preferredName === formValues.secondName}
                      disabled={isDisabled}
                      onChange={(e) => {
                        const { value } = e.currentTarget;
                        changeData({ name: 'preferredName', value });
                      }}
                    />
                    <Label text={formValues.secondName} htmlFor='PreferName' />
                  </div>
                </>
              }
            </div>
          </div>
          <article className='section-form__article'>
            {isDisabled ? (
              <Input
                id='bornDate'
                label='Fecha de nacimiento'
                placeholder='Selecciona'
                type='text'
                value={formValues.bornDate}
                disabled
              />

            ) : (
              <>
                <Label text={'Fecha de nacimiento'} htmlFor='bornDate' />

                <ACalendar
                  birthdateForm={
                    formValues.bornDate ? moment(formValues.bornDate, 'YYYY-MM-DD') : undefined
                  }
                  updateBirthdate={(date) => changeData({ name: 'bornDate', value: date })}
                  disabled={isDisabled}
                  setMarginForCalendar={setMarginForCalendar}
                  isOpenCalendar={isOpenCalendar}
                  setIsOpenCalendar={setIsOpenCalendar}
                  error={!!errors.bornDate}

                />
                {errors.bornDate && (
                  <div className='subtext-error'>
                    <WarningIcon />
                    <span>Campo obligatorio</span>
                  </div>
                )}
              </>
            )}
          </article>
        </aside>

        <aside className='section-form__aside'>
          <article className='section-form__article'>
            {isDisabled ? (
              <Input
                id='documentCountry'
                label='País de documento'
                placeholder=''
                type='text'
                value={documentCountry}
                disabled
              />
            ) : (
              <CountrySelect
                textLabel='País de documento'
                htmlForLabel='documentCountry'
                placeholder='Seleccione uno'
                error={false}
                handleChange={(value) => {
                  setDocumentCountry(value || '');
                  setValue('idType', '');
                  trigger(['idType']);
                }}
                value={{
                  label: documentCountry as any,
                  value: documentCountry as any,
                }}
                onExternalCodeChange={(externalCode) => { }}
                defaultValue={profileData?.country}
                allowedCountries={[
                  'Colombia',
                  'Guatemala',
                  'Panamá',
                  'México',
                  'Costa Rica',
                  'Perú',
                ]}
              />
            )}
          </article>
          <article className='section-form__article'>
            <Controller
              control={control}
              name='idType'
              render={({ field: { onChange, value, ...field }, fieldState: { error } }) =>
                isDisabled ? (
                  <Input
                    id='idType'
                    label='Tipo de documento'
                    placeholder=''
                    type='text'
                    value={formValues.idType}
                    disabled
                  />
                ) : (
                  <DocumentTypeSelect
                    country={documentCountry}
                    value={{ value, label: value }}
                    handleChange={(value) => {
                      onChange(value);
                      changeData({ name: 'idType', value });
                    }}
                    textLabel='Tipo de documento'
                    htmlForLabel='idType'
                    error={!!errors.idType}
                    {...field}
                  />
                )
              }
            />
          </article>
        </aside>

        <aside className='section-form__aside'>
          <article className='section-form__article'>
            <Input
              id='numberId'
              label='Número de documento'
              placeholder='Tú número de documento'
              value={formValues.numberId}
              onChange={(e) => {
                const { value } = e.currentTarget;
                changeData({ name: 'numberId', value });
              }}
              type='text'
              disabled={isDisabled}
              register={register}
              error={!!errors.numberId}
            />
          </article>
          {isDisabled ? (
            <Input
              id='bloodType'
              label='Grupo sanguíneo (opcional)'
              placeholder='Tú grupo sanguíneo'
              type='text'
              value={formValues.bloodType}
              disabled
            />
          ) : (
            <Select
              htmlForLabel={'bloodType'}
              selected={formValues.bloodType}
              disabled={isDisabled}
              placeholder={'Tú grupo sanguíneo'}
              handleChange={(value) => changeData({ name: 'bloodType', value })}
              value={{ value: formValues.bloodType, label: formValues.bloodType }}
              options={bloodGroup?.map((value) => ({
                value,
                label: value,
              }))}
              textLabel={'Grupo sanguíneo (opcional)'}
              error={!!errors.bloodType}
            />
          )}
        </aside>
      </section>
    </Section>
  );
};
